import { Component, inject, OnInit } from '@angular/core';
import { NavController, Platform, PopoverController, } from '@ionic/angular';
import { of, Subject } from 'rxjs';
import { catchError, filter, take, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { NetworkService } from 'src/app/services/network/network.service';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { ActivatedRoute } from '@angular/router';
import { Logger, LoggingService } from 'ionic-logging-service';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { HttpClient } from '@angular/common/http';
import { TenantService } from '@app/services/tenant/tenant.service';
import { DialogService } from '@app/services/dialog/dialog.service';
import { LogoutService } from '@app/services/logout/logout.service';
import { PageTitleService } from '@app/services/page-title/page-title.service';

@Component({
  selector: 'app-network-check',
  templateUrl: './network-check.page.html',
  styleUrls: ['./network-check.page.scss'],
})
export class NetworkCheckPage implements OnInit {
  private route = inject(ActivatedRoute);

  public unSubscribe = new Subject<void>();
  public isCordova: boolean = this.platformService.isCordova();
  public isOnline = this.networkService.hasInternetAccess;
  public errorMessage: string = null;
  public showUnsupportedVersionMessage: boolean = false;
  public APP_ID: string = 'id1391512097';
  public APP_PACKAGE_NAME: string = 'net.printix.printixapp';
  public EULA_ACCEPTED_STORAGE_STRING = 'eula-accepted';
  public EULA_ACCEPTED_STORAGE_KEY = 'is-eula-accepted';
  public showEulaText = false;
  private registrationCode: string = null;
  private refreshTokenParam: string = null;
  private queryParams: { [key: string]: string | undefined };
  private signOutParam: string | null = null;
  private logger: Logger;

  private urlTenantDomain: string;
  public  signInCode: string | null = null;
  private endpointLogInWepAppTenant: string;
  private endpointLogInResponse_WepApp: string;
  public eulaHtml: string;

  constructor (
    private authService: AuthService,
    private dialogService: DialogService,
    private environmentService: EnvironmentService,
    private loggingService: LoggingService,
    private logoutService: LogoutService,
    public navController: NavController,
    private networkService: NetworkService,
    private pageTitleService: PageTitleService,
    public platform: Platform,
    public popoverCtrl: PopoverController,
    private platformService: PlatformService,
    private storageService: StorageService,
    private tenantService: TenantService,
    private httpClient: HttpClient
  ) {
    this.logger = loggingService.getLogger("[NetworkCheckPage]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  ngOnInit() {
    this.pageTitleService.setTitle('');
    this.platform.ready().then(() => {
      this.logger.info('ngOnInit() - Platform is ready');
      if (this.isCordova) {
        this.storageService.getDatabaseState().pipe(
          filter(isReady => isReady),
          take(1)
          // check localstorage for 'EULA accepted'
        ).subscribe(() => this.checkForAcceptedEula());
      } else {
        this.setUrlTenantdomain();
        this.setQueryParams();
      }
    });
  }

  private setUrlTenantdomain() {
    this.logger.info('setUrlTenantdomain()');
    this.urlTenantDomain = location.hostname;
    this.tenantService.setTenantInputValues(this.urlTenantDomain, null);
  }

  private checkForAcceptedEula(): void {
    this.logger.info('checkForAcceptedEula()');
    this.storageService.getItemFromLocalStorage(this.EULA_ACCEPTED_STORAGE_KEY, 'checkForAcceptedEula()')
    .pipe(takeUntil(this.unSubscribe))
    .subscribe((EULA_ACCEPTED) => {
      if (EULA_ACCEPTED === this.EULA_ACCEPTED_STORAGE_STRING) {
        this.setQueryParams();
      } else {
        this.showEulaText = true;
        this.loadEulaText();
      }
      this.unSubscribe.next();
      this.unSubscribe.complete();
    });
  }

  loadEulaText() {
    this.httpClient.get('assets/eula-text/eula.html', { responseType: 'text' }).subscribe(html => {
      this.eulaHtml = html;
    });
  }

  public eulaAccepted() {
    this.storageService.addItemToLocalStorage(this.EULA_ACCEPTED_STORAGE_KEY, this.EULA_ACCEPTED_STORAGE_STRING)
    .pipe(takeUntil(this.unSubscribe))
    .subscribe(() => {
      this.showEulaText = false;
      this.setQueryParams();
    })
  }

  public eulaDenied() {
    this.showEulaText = false;
    navigator['app'].exitApp();
  }

  private setQueryParams(): void {
    this.logger.info('setQueryParams()');
    this.route.queryParams.pipe(take(1))
    .subscribe(params => {
      this.queryParams = params;
      this.signInCode = this.queryParams["code"] ? this.queryParams["code"] : null;
      this.registrationCode = this.queryParams['registrationCode'] ? this.queryParams['registrationCode'] : null;
      this.refreshTokenParam = this.queryParams['refreshToken'] ? this.queryParams['refreshToken'] : null;
      this.signOutParam = params['signOutParam'] ? params['signOutParam'] : null;
      if (this.signOutParam) {
        this.logger.info('signOutParam' + this.signOutParam);
        this.handleUnverifiedCredentials();
      } else {
        this.setEnvironmentPrefix();
      }
    });
  }

  private setEnvironmentPrefix(): void {
    if (this.isCordova) {
      this.environmentService.setEnvironmentPrefixFromStorage().subscribe((environmentPrefix) => {
        // this.checkAppVersionState();
        // outcommented call to checkAppVersionState(), since it has not been used for years
        // and it needs a reworking to accomodate multiple datacenters, both for the endpoint and
        // also not to be checked so early in the app, before the currentEnvironmentEndpoints are set.
        this.verifyUserCredentials();
      });
    } else {
      if (!this.urlTenantDomain.includes('localhost')) {
        this.environmentService.setEnvironmentVariablesFromUrl(this.urlTenantDomain).subscribe((response) => {
          // this.checkAppVersionState();
          // outcommented call to checkAppVersionState(), since it has not been used for years
          // and it needs a reworking to accomodate multiple datacenters, both for the endpoint and
          // also not to be checked so early in the app, before the currentEnvironmentEndpoints are set.
          this.verifyUserCredentials();
        });
      } else {
        this.verifyUserCredentials();
      }
    }
  }
 private verifyUserCredentials(): void {
    const methodName = 'verifyUserCredentials() '
    this.logger.info(methodName);
    if (this.refreshTokenParam) {
      this.logger.info(methodName + 'url includes "refreshToken="');
      this.logInFromTenantHub();
    } else {
      this.logger.info(methodName + 'url does NOT include "refreshToken="');
      this.authService.verifyUserCredentials()
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(() => {
        this.unSubscribe.next();
        this.unSubscribe.complete();
        this.authSuccessful();
      }, () => {
        this.unSubscribe.next();
        this.unSubscribe.complete();
        if (this.signInCode) {
          this.handleUserAuthentication();
        } else {
          this.handleUnverifiedCredentials();
        }
      });
    }
  }

  private handleUnverifiedCredentials() {
    const methodName = 'handleUnverifiedCredentials() '
    this.logger.info(methodName);
    this.logger.info(methodName + 'url does not contain "code="');
    if (!this.isCordova && !this.urlTenantDomain.includes('localhost')) { // Webapp, not in development
      if (this.signOutParam === 'ACCESS_DENIED') {
        this.dialogService.showAlertDialog('ACCESS_DENIED')
        .subscribe(() => {
          this.goToBrowserSignIn();
        });
      } else {
        this.goToBrowserSignIn();
      }
    }  else {
      this.logger.info(methodName + 'Go to login page');
      this.navController.navigateRoot(['/sign-in']);
    }
  }

  private goToBrowserSignIn(): void {
    this.endpointLogInWepAppTenant = this.environmentService.getEnvironmentEndpoint['endpointLogInWepAppTenant'];
    this.endpointLogInResponse_WepApp = this.setSignInResponse();

    const signInAppUrl = this.endpointLogInWepAppTenant + this.urlTenantDomain + this.endpointLogInResponse_WepApp + this.urlTenantDomain;
    this.logger.info('goToBrowserSignIn() - signInAppUrl: ' + signInAppUrl);
    window.open(signInAppUrl, '_self');

    // TEST-URL for testing registrationCode functionality in regard to Printix GO and on-device
    // let alternateSignInAppUrl = this.endpointLogInWepAppTenant + 'printix70.printix.net?registrationCode=FORGOT_CODE' + this.endpointLogInResponseForgotCode_WepApp + 'printix70.printix.net?registrationCode=FORGOT_CODE';
    // let alternateSignInAppUrl = this.endpointLogInWepAppTenant + 'printix29.devenv.printix.net' + this.endpointLogInResponse_WepApp + 'printix29.devenv.printix.net';
    // window.open(alternateSignInAppUrl, '_self');
  }

   private setSignInResponse(): string {
    this.logger.info('setUrlWithRegistrationCode()');
    const url = !this.registrationCode
           ?  this.environmentService.getEnvironmentEndpoint['endpointLogInResponse_WepApp']
           :  this.environmentService.getEnvironmentEndpoint['endpointLogInResponseForgotCode_WepApp'] + '?registrationCode=' + this.registrationCode;
    return url;
  }

  private handleUserAuthentication(): void {
    this.logger.info('handleUserAuthentication()');
    this.authService.authenticateSignedInUser(this.signInCode, this.urlTenantDomain).pipe(
      take(1),
      catchError(error => {
        this.logger.error('signInUser() - loginService.signInUser() ERROR: ' + JSON.stringify(error));
        this.handleSignOut(error);
        return of(error);
      })
    ).subscribe(() => {
      this.logger.info('signInUser() - loginService.signInUser() COMPLETE');
      return this.authSuccessful();
    });
  }

  private handleSignOut(error): void {
    this.logger.info('handleSignOut()');
    this.authService.deAuthenticateUser().pipe(take(1))
    .subscribe({
      next: () => {
        this.logoutService.signOutUser().subscribe({
          next: () => {
            const unauthenticatedUser = error.status === 401 || error.status === 403 ? true : false;
            const queryParams = unauthenticatedUser ? { signOutParam: 'ACCESS_DENIED' } : {};
            const path = this.isCordova ? '/sign-in' : '/network-check';
            this.navController.navigateRoot([path], { queryParams });
          },
          error: (error) => {
            this.logger.error('handleSignOut() - logoutService.signOutUser() ERROR: ' + JSON.stringify(error));
          }
        });
      },
      error: (error) => {
        this.logger.error('handleSignOut() - authService.deAuthenticateUser() ERROR: ' + JSON.stringify(error));
      }
    });
  }

  public authSuccessful() {
    // continue the login process
    this.logger.info('authSuccessful()');
    this.route.queryParams.pipe(take(1))
    .subscribe(params => {
      const queryParams = params['registrationCode'] ? { registrationCode: params['registrationCode']} : {};
      this.navController.navigateRoot(['/tenant-select'], { queryParams });
    });
  }

  private logInFromTenantHub() {
    this.logger.info('logInFromTenantHub()');
    let refreshToken = location.hash.split('=')[1];
    this.authService.refreshAccessTokenFromUrl(refreshToken)
    .subscribe(() => {
      this.authSuccessful();
    });
  }
}